import type { ObjectiveQuantity } from '../schemas/protocol.js';
import type { SnapshotExtract } from '../schemas/turn.js';

/**
 * The MATTER of an objective (refonte modulaire §8.1 point 4, §8.2): what the
 * scribe attributes, what the code keeps, and what it counts.
 *
 * « La matière d'un objectif, ce sont ces extraits et rien d'autre. La quantité
 * attendue est une règle de code sur du texte extrait, jamais une estimation du
 * modèle. » Everything in this file exists to hold that line: the model points
 * at pieces of the person's verbatim, and nothing it says about how much was
 * said is believed.
 */

/**
 * The apostrophes and the blanks two sides spell differently.
 *
 * A model answers `j'ai` where the person typed `j’ai`, or a line break where
 * the textarea sent a non-breaking space. Neither is a paraphrase, and both
 * would lose a legitimate extract - so the comparison is made on a normalised
 * form while the stored text keeps the scribe's own spelling. Nothing else is
 * folded: lowercasing the text would let « Ma Mère » through for « ma mere »,
 * and at that point the check has stopped being about literality.
 */
const APOSTROPHES = /[‘’‛ʼ´`']/g;

const WORD = /[\p{L}\p{N}]/u;

/** The form the literality check compares on - never the form it stores. */
export function normalizeMatter(text: string): string {
  return text.replace(APOSTROPHES, "'").replace(/\s+/g, ' ').trim();
}

function isWord(character: string): boolean {
  return character !== '' && WORD.test(character);
}

/**
 * Is `needle` really IN `haystack`, on word boundaries.
 *
 * The boundary is what separates an extract from a truncation. `manger` holds
 * `mange`, so a plain `includes` accepts an extract the scribe cut mid-word -
 * and a cut extract read back in Sessions is a sentence the person never said.
 * The boundary is only required on an edge that is itself a word character:
 * an extract that opens on a quote or closes on a comma is quoting, not
 * truncating.
 */
function containsLiterally(haystack: string, needle: string): boolean {
  const first = needle[0] ?? '';
  const last = needle[needle.length - 1] ?? '';
  const openBound = isWord(first);
  const closeBound = isWord(last);
  let from = 0;
  for (;;) {
    const at = haystack.indexOf(needle, from);
    if (at === -1) return false;
    const before = at === 0 ? '' : (haystack[at - 1] ?? '');
    const after = haystack[at + needle.length] ?? '';
    if ((!openBound || !isWord(before)) && (!closeBound || !isWord(after))) return true;
    from = at + 1;
  }
}

/**
 * The extracts of a turn that are really in the person's reply (§8.1 point 4).
 *
 * « Un extrait absent du verbatim de la personne est rejeté sans casser le
 * tour » - so this returns what survives and never throws.
 *
 * What comes back is the scribe's own text with its blanks collapsed, in the
 * order it was given, without the duplicates one report may carry twice. Its
 * APOSTROPHES are left alone: the comparison folds them, the matter a reader
 * sees keeps the French it was written in.
 */
export function literalExtracts(reply: string, extracts: readonly string[]): string[] {
  const haystack = normalizeMatter(reply);
  const kept: string[] = [];
  const seen = new Set<string>();
  for (const extract of extracts) {
    const text = extract.replace(/\s+/g, ' ').trim();
    const needle = normalizeMatter(text);
    if (needle === '' || seen.has(needle)) continue;
    if (!containsLiterally(haystack, needle)) continue;
    seen.add(needle);
    kept.push(text);
  }
  return kept;
}

/**
 * The matter of an objective after this turn: what it held, plus what the turn
 * adds (H19 - a score is noted on the cumulated matter, not on one reply).
 *
 * A text already held LIVE is not added twice; one already held INVALIDATED is,
 * because « sa matière vivante repart de zéro » (§8.4) and a person who says
 * the thing again after an invalidation has said it again.
 */
export function accumulateMatter(
  existing: readonly SnapshotExtract[],
  added: readonly string[],
): SnapshotExtract[] {
  const live = new Set(
    existing
      .filter((extract) => !extract.invalidated)
      .map((extract) => normalizeMatter(extract.text)),
  );
  const accumulated = [...existing];
  for (const text of added) {
    const key = normalizeMatter(text);
    if (key === '' || live.has(key)) continue;
    live.add(key);
    accumulated.push({ text, invalidated: false });
  }
  return accumulated;
}

/** The texts that still count - an invalidated extract stays in the journal (§8.4). */
export function liveExtracts(extracts: readonly SnapshotExtract[]): string[] {
  return extracts.filter((extract) => !extract.invalidated).map((extract) => extract.text);
}

/** Sentences and characters, as §7.2 asks them of a « quantité de matière attendue ». */
export type MatterQuantity = {
  readonly sentences: number;
  readonly chars: number;
};

export const EMPTY_MATTER_QUANTITY: MatterQuantity = { sentences: 0, chars: 0 };

/**
 * How much a text says: its sentences and its characters, counted in code.
 *
 * A sentence is a run of text between terminators, and a run of terminators is
 * ONE terminator - « ... » ends one sentence and not three. Characters are
 * counted on the normalised text and by code point, because a minimum in
 * characters is about how much the person said and a surrogate pair is one
 * character to everyone but a UTF-16 index.
 */
export function countQuantity(text: string): MatterQuantity {
  const normalised = normalizeMatter(text);
  if (normalised === '') return EMPTY_MATTER_QUANTITY;
  const sentences = normalised
    .split(/[.!?…]+/)
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '').length;
  return { sentences, chars: [...normalised].length };
}

/**
 * The quantity of a whole matter: the sentences of each live extract SUMMED,
 * and the characters of the lot.
 *
 * Summed and not counted on the concatenation, because two extracts that carry
 * no full stop are two things the person said and not one sentence - and
 * concatenating them would make « trois phrases minimum » unreachable for a
 * person who answers in fragments, which is how people answer.
 */
export function matterQuantity(extracts: readonly SnapshotExtract[]): MatterQuantity {
  const live = liveExtracts(extracts);
  const sentences = live.reduce((total, text) => total + countQuantity(text).sentences, 0);
  return { sentences, chars: countQuantity(live.join(' ')).chars };
}

/**
 * Is the expected quantity reached (§7.2)? A minimum nobody posed is reached.
 *
 * `>=` on both, and deliberately: « N phrases minimum » is met at N, not at
 * N + 1.
 */
export function quantityReached(expected: ObjectiveQuantity, counted: MatterQuantity): boolean {
  if (expected.minSentences !== null && counted.sentences < expected.minSentences) return false;
  if (expected.minChars !== null && counted.chars < expected.minChars) return false;
  return true;
}
